// frontend/src/pages/SumUpCheckout.js
// Page de paiement SumUp — charge le widget carte officiel de SumUp
// (SumUp ne propose pas de page hébergée accessible par simple redirection ;
// le paiement doit se faire via leur SDK JS intégré dans notre propre page).
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, VStack, Heading, Text, Spinner, Alert, AlertIcon,
  AlertTitle, AlertDescription, Button, Icon,
} from '@chakra-ui/react';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { apiService } from '../services/apiService';

const SDK_URL = 'https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js';

export default function SumUpCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const checkoutId = searchParams.get('checkout_id');

  const [status, setStatus] = useState('loading'); // loading, ready, processing, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!checkoutId) {
      setStatus('error');
      setErrorMsg('Identifiant de paiement manquant');
      return;
    }

    let scriptEl = document.querySelector(`script[src="${SDK_URL}"]`);

    const mountWidget = () => {
      if (mountedRef.current) return;
      mountedRef.current = true;

      if (!window.SumUpCard) {
        setStatus('error');
        setErrorMsg('Impossible de charger le module de paiement SumUp');
        return;
      }

      window.SumUpCard.mount({
        id: 'sumup-card-widget',
        checkoutId,
        showSubmitButton: true,
        showFooter: false,
        locale: 'fr-FR',
        onResponse: (type, body) => {
          setStatus('processing');
          // Dans les deux cas (succès affiché par le widget ou erreur carte),
          // on interroge le backend qui vérifie réellement le statut auprès de
          // SumUp — cela permet aussi de marquer la cotisation comme 'refuse'
          // au lieu de rester 'en_attente' indéfiniment en cas d'échec réel.
          apiService.makeRequest(`/payments/sumup/verify/${checkoutId}`)
            .then((data) => {
              if (data.is_paid) {
                setStatus('success');
              } else {
                setStatus('error');
                setErrorMsg(
                  type === 'error'
                    ? (body?.message || 'Le paiement a échoué')
                    : 'Le paiement n\'a pas pu être confirmé. Contactez l\'administration si le montant a bien été débité.'
                );
              }
            })
            .catch(() => {
              setStatus('error');
              setErrorMsg(type === 'error' ? (body?.message || 'Le paiement a échoué') : 'Erreur lors de la vérification du paiement.');
            });
        },
      });
      setStatus('ready');
    };

    if (window.SumUpCard) {
      mountWidget();
    } else if (scriptEl) {
      scriptEl.addEventListener('load', mountWidget);
    } else {
      scriptEl = document.createElement('script');
      scriptEl.src = SDK_URL;
      scriptEl.async = true;
      scriptEl.onload = mountWidget;
      scriptEl.onerror = () => {
        setStatus('error');
        setErrorMsg('Impossible de charger le module de paiement SumUp');
      };
      document.body.appendChild(scriptEl);
    }

    return () => {
      if (scriptEl) scriptEl.removeEventListener('load', mountWidget);
    };
  }, [checkoutId]);

  const handleClose = () => {
    // Tente de fermer la popup (fonctionne même avec noopener, tant que la
    // fenêtre a été ouverte via window.open). Si ça échoue (navigation
    // directe, pas une popup), on retombe sur une navigation classique.
    window.close();
    setTimeout(() => {
      if (!window.closed) navigate('/dashboard');
    }, 300);
  };

  return (
    <Container maxW="container.sm" py={10}>
      <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
        <VStack spacing={6} align="stretch">
          <Heading size="md" textAlign="center">💳 Paiement SumUp</Heading>

          {status === 'loading' && (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color="green.500" />
              <Text color="gray.600">Chargement du module de paiement…</Text>
            </VStack>
          )}

          {status === 'error' && (
            <VStack spacing={4}>
              <Icon as={FaExclamationTriangle} boxSize={12} color="red.500" />
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Paiement impossible</AlertTitle>
                  <AlertDescription>{errorMsg}</AlertDescription>
                </Box>
              </Alert>
              <Button colorScheme="blue" onClick={handleClose}>Fermer</Button>
            </VStack>
          )}

          {status === 'processing' && (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color="green.500" />
              <Text color="gray.600">Vérification du paiement…</Text>
            </VStack>
          )}

          {status === 'success' && (
            <VStack spacing={4}>
              <Icon as={FaCheckCircle} boxSize={16} color="green.500" />
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Paiement réussi !</AlertTitle>
                  <AlertDescription>Votre cotisation a été enregistrée avec succès.</AlertDescription>
                </Box>
              </Alert>
              <Button colorScheme="green" onClick={handleClose}>Fermer</Button>
            </VStack>
          )}

          {/* Le widget SumUp se monte toujours dans ce conteneur, même
              pendant "loading", pour que le SDK trouve l'élément au chargement */}
          <Box id="sumup-card-widget" display={status === 'ready' ? 'block' : 'none'} />
        </VStack>
      </Box>
    </Container>
  );
}
